import React, {useEffect, useState} from 'react';
import {TextInput} from 'react-native';
import styled from 'styled-components/native';
import {RFValue} from 'react-native-responsive-fontsize';
import {cpf as cpfValidator} from 'cpf-cnpj-validator';
import {AuthInput} from '../../../Interface/Forms/Inputs/AuthInput';
import {IconHeader} from '../../../Interface/Headers/Header/styles';
import {
  BackgroundHeader,
  Footer,
  Form,
  FormControl,
  FormHeader,
  FormInfos,
  IconButton,
  Label,
  Subtitle,
  Title,
} from '../styles';
import {Button} from '../../../Interface/Forms/Buttons/Button';
import {api} from '../../../../services/api';
import theme from '../../../../global/styles/legacy/theme';

const RESEND_COOLDOWN = 60;

const SmsRow = styled.View<{hasError?: boolean}>`
  flex-direction: row;
  border-bottom-width: 2px;
  border-color: ${({theme: t, hasError}) =>
    hasError ? t.colors.attention : t.colors.border};
  height: ${RFValue(45)}px;
  align-items: center;
`;

const SmsInput = styled(TextInput)`
  flex: 1;
  font-size: ${RFValue(12)}px;
  color: ${({theme: t}) => t.colors.title};
  font-family: ${({theme: t}) => t.fonts.medium};
  height: ${RFValue(45)}px;
`;

const ResendButton = styled.TouchableOpacity`
  background-color: ${({theme: t}) => t.colors.text_dark};
  border-radius: ${RFValue(20)}px;
  padding: ${RFValue(4)}px ${RFValue(10)}px;
  margin-left: ${RFValue(8)}px;
  align-items: center;
  justify-content: center;
`;

const ResendButtonText = styled.Text`
  color: ${({theme: t}) => t.colors.background};
  font-size: ${RFValue(10)}px;
  font-family: ${({theme: t}) => t.fonts.semibold};
`;

const ErrorText = styled.Text`
  color: ${({theme: t}) => t.colors.attention};
  font-size: ${RFValue(10)}px;
  font-family: ${({theme: t}) => t.fonts.medium};
  margin-top: ${RFValue(6)}px;
`;

interface Props {
  onBack: () => void;
  onSuccess: () => void;
  fromChangePassword?: boolean;
}

type Step = 'cpf' | 'reset' | 'success';

const PASSWORD_SPECIAL = /[!@#$%^&*()\-_=+{};:,<.>/?\\|[\]~`'"]/;

function validatePasswordRules(value: string): string {
  if (value.length < 8) {
    return 'Mínimo de 8 caracteres.';
  }
  if (!/[A-Z]/.test(value)) {
    return 'Inclua pelo menos 1 letra maiúscula.';
  }
  if (!/\d/.test(value)) {
    return 'Inclua pelo menos 1 número.';
  }
  if (!PASSWORD_SPECIAL.test(value)) {
    return 'Inclua pelo menos 1 caractere especial.';
  }
  return '';
}

export default function ForgotPasswordForm({
  onBack,
  onSuccess,
  fromChangePassword,
}: Props) {
  const [step, setStep] = useState<Step>('cpf');

  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [cpfIsValid, setCpfIsValid] = useState(false);
  const [isLoadingCpf, setIsLoadingCpf] = useState(false);

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isLoadingReset, setIsLoadingReset] = useState(false);

  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timeout = setTimeout(() => setResendCooldown(s => s - 1), 1000);
    return () => clearTimeout(timeout);
  }, [resendCooldown]);

  function handleCpfChangeText(raw: string, extracted: string) {
    setCpfIsValid(false);
    if (/[a-zA-Z@]/.test(raw)) {
      setCpfError('Digite um CPF válido (apenas números)');
      return;
    }
    setCpfError('');
    setCpf(extracted);
  }

  async function validateCpf() {
    const isValid = cpfValidator.isValid(cpf);
    setCpfIsValid(isValid);
    if (!isValid) {
      setCpfError('CPF inválido');
    }
  }

  useEffect(() => {
    if (cpf.length === 11) {
      validateCpf();
    }
  }, [cpf]);

  async function sendRecoveryCode() {
    setIsLoadingCpf(true);
    try {
      await api.post('/request_password_token', {cpf});
      setStep('reset');
      setCode('');
      setResendCooldown(RESEND_COOLDOWN);
    } catch (error: any) {
      const msg =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        'Não foi possível enviar o código. Tente novamente.';
      setCpfError(msg);
    } finally {
      setIsLoadingCpf(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0) {
      return;
    }
    setCode('');
    setResendCooldown(RESEND_COOLDOWN);
    try {
      await api.post('/request_password_token', {cpf});
    } catch {}
  }

  async function onSubmitReset() {
    setIsLoadingReset(true);
    try {
      await api.post('/confirm_password_token', {
        cpf,
        token: code,
        password: password,
        password_confirmation: confirmPassword,
      });
      if (fromChangePassword) {
        onSuccess();
      } else {
        setStep('success');
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        'Erro ao redefinir senha. Tente novamente.';
      const tipo = error?.response?.data?.tipo;
      if (tipo === 'token' || tipo === 'codigo') {
        setCodeError(msg);
      } else if (tipo === 'password' || tipo === 'senha') {
        setPasswordError(msg);
      } else {
        setCodeError(msg);
      }
    } finally {
      setIsLoadingReset(false);
    }
  }

  if (step === 'success') {
    return (
      <>
        <BackgroundHeader>
          <IconButton onPress={onSuccess}>
            <IconHeader name="chevron-left" />
          </IconButton>
        </BackgroundHeader>
        <Form>
          <FormHeader>
            <FormInfos>
              <Title>Senha redefinida!</Title>
              <Subtitle>
                Sua senha foi alterada com sucesso. Faça login para continuar.
              </Subtitle>
            </FormInfos>
          </FormHeader>
        </Form>
        <Footer>
          <Button title="Fazer login" onPress={onSuccess} enabled />
        </Footer>
      </>
    );
  }

  if (step === 'reset') {
    return (
      <>
        <BackgroundHeader>
          <IconButton onPress={() => setStep('cpf')}>
            <IconHeader name="chevron-left" />
          </IconButton>
        </BackgroundHeader>
        <Form>
          <FormHeader>
            <FormInfos>
              <Title>Redefinir senha</Title>
              <Subtitle>
                Digite o código enviado por SMS e sua nova senha.
              </Subtitle>
            </FormInfos>
          </FormHeader>

          <FormControl>
            <Label>Código SMS</Label>
            <SmsRow hasError={!!codeError}>
              <SmsInput
                placeholder="000000"
                placeholderTextColor={theme.colors.placeholder}
                keyboardType="number-pad"
                autoComplete="sms-otp"
                textContentType="oneTimeCode"
                maxLength={6}
                value={code}
                onChangeText={(v: string) => {
                  setCode(v);
                  setCodeError('');
                }}
              />
              <ResendButton
                onPress={handleResend}
                activeOpacity={0.8}
                disabled={resendCooldown > 0}
                style={{opacity: resendCooldown > 0 ? 0.5 : 1}}>
                <ResendButtonText>
                  {resendCooldown > 0
                    ? `${String(Math.floor(resendCooldown / 60)).padStart(
                        2,
                        '0',
                      )}:${String(resendCooldown % 60).padStart(2, '0')}`
                    : 'Reenviar'}
                </ResendButtonText>
              </ResendButton>
            </SmsRow>
            {!!codeError && <ErrorText>{codeError}</ErrorText>}
          </FormControl>

          <FormControl>
            <Label>Nova senha</Label>
            <AuthInput
              errorStyle={!!passwordError}
              error={passwordError}
              placeholder="* * * * * * * *"
              keyboardType="default"
              autoCapitalize="none"
              value={password}
              onChangeText={(v: string) => {
                setPassword(v);
                setPasswordError('');
              }}
              onBlur={() => setPasswordError(validatePasswordRules(password))}
              passwordInput
            />
          </FormControl>

          <FormControl>
            <Label>Confirmar nova senha</Label>
            <AuthInput
              errorStyle={!!confirmPasswordError}
              error={confirmPasswordError}
              placeholder="* * * * * * * *"
              keyboardType="default"
              autoCapitalize="none"
              value={confirmPassword}
              onChangeText={(v: string) => {
                setConfirmPassword(v);
                setConfirmPasswordError('');
              }}
              onBlur={() => {
                if (confirmPassword && confirmPassword !== password) {
                  setConfirmPasswordError('As senhas não coincidem.');
                }
              }}
              passwordInput
            />
          </FormControl>
        </Form>
        <Footer>
          <Button
            title="Confirmar"
            onPress={onSubmitReset}
            enabled
            loading={isLoadingReset}
          />
        </Footer>
      </>
    );
  }

  return (
    <>
      <BackgroundHeader>
        <IconButton onPress={onBack}>
          <IconHeader name="chevron-left" />
        </IconButton>
      </BackgroundHeader>
      <Form>
        <FormHeader>
          <FormInfos>
            <Title>Recuperar senha</Title>
            <Subtitle>
              Digite o CPF da sua conta para receber um código de recuperação
              por SMS.
            </Subtitle>
          </FormInfos>
        </FormHeader>

        <FormControl>
          <Label>CPF</Label>
          <AuthInput
            errorStyle={!!cpfError}
            error={cpfError}
            placeholder="000.000.000-00"
            keyboardType="number-pad"
            onChangeText={(raw: string, extracted: any) =>
              handleCpfChangeText(raw, extracted)
            }
            mask={'[000].[000].[000]-[00]'}
          />
        </FormControl>
      </Form>
      <Footer>
        <Button
          title="Enviar código"
          onPress={sendRecoveryCode}
          enabled={cpfIsValid}
          loading={isLoadingCpf}
        />
      </Footer>
    </>
  );
}
